import { computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Item } from 'devextreme/ui/tree_view_types';
import { hasPermission } from 'fido-types';
import { createInjectionToken } from 'ngxtension/create-injection-token';
import { filter, map, startWith } from 'rxjs/operators';
import { injectUser } from './auth/data/user';
import { injectIntakeSessions } from './inquiries/data/intake-sessions';
import { injectCurrentMembership } from './organizations/data/current-membership';
import { injectCurrentOrganization } from './organizations/data/current-organization';

export const [injectAppNavigation, provideAppNavigation] = createInjectionToken(() => {
  const user = injectUser();
  const organization = injectCurrentOrganization();
  const membership = injectCurrentMembership();
  const sessionsResource = injectIntakeSessions();
  const router = inject(Router);

  const inquiryBadgeCount = computed(() => {
    const sessions = sessionsResource.value() ?? [];
    return sessions.filter(
      (s) =>
        (s.status === 'submitted' || s.status === 'timed_out') &&
        s.inquiryStatus != null &&
        s.inquiryStatus !== 'retained' &&
        s.inquiryStatus !== 'not_a_fit',
    ).length;
  });

  const currentUrl = toSignal(
    router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => router.url),
      startWith(router.url),
    ),
  );

  const markSelected = (
    item: Item & { path?: string },
    url: string | undefined,
  ): Item & { path?: string } => {
    const result: Item & { path?: string } = {
      ...item,
      selected: item.path ? url === item.path : false,
    };
    if (item.items) {
      result.items = item.items.map((child) => markSelected(child, url));
    }
    return result;
  };

  return computed<(Item & { path?: string; active?: boolean })[]>(() => {
    const u = user();
    const o = organization.value();
    const url = currentUrl();
    const m = membership.value();

    if (!u || !o) return [];

    const canManageSettings =
      m?.status === 'active' && !!m.role && hasPermission(m.role, 'manage_settings');
    const canAccessContacts =
      m?.status === 'active' && !!m.role && hasPermission(m.role, 'view_contacts');
    const canViewConsultationCalendar =
      m?.status === 'active' && !!m.role && hasPermission(m.role, 'manage_consultation');
    const canManageEngagementTemplates =
      m?.status === 'active' && !!m.role && hasPermission(m.role, 'manage_engagement_templates');

    const isOwner = m?.status === 'active' && m.role === 'owner';

    const firmManagementChildren = [
      { text: 'Members', path: '/members' },
      ...(o.settings.teamsEnabled ? [{ text: 'Teams', path: '/teams' }] : []),
      ...(canManageEngagementTemplates
        ? [{ text: 'Templates', path: '/engagement/templates' }]
        : []),
      ...(canManageSettings
        ? [
            { text: 'Invitations', path: '/invitations' },
            { text: 'Activity', path: '/activity' },
            { text: 'Settings', path: '/settings' },
          ]
        : []),
    ];

    const baseNav = [
      {
        text: 'Home',
        icon: 'home',
        path: '/home',
      },
      ...(canAccessContacts
        ? [
            {
              text: 'Contacts',
              icon: 'group',
              path: '/contacts',
            },
          ]
        : []),
      {
        text: 'Inquiries',
        icon: 'email',
        path: '/inquiries',
        template: 'inquiriesBadge',
        badge: inquiryBadgeCount(),
      },
      ...(canViewConsultationCalendar
        ? [
            {
              text: 'Calendar',
              icon: 'event',
              path: '/consultations',
            },
          ]
        : []),
      ...(isOwner
        ? [
            {
              text: 'Firm Management',
              icon: 'toolbox',
              items: firmManagementChildren,
            },
          ]
        : []),
    ];

    return baseNav.map((item) => markSelected(item, url));
  });
});
